import React from 'react'
import cn from 'classnames'
import { Modal } from 'antd'
import { parsePlan, pluralN } from './genUtils'
import s from './gen.module.scss'
import p from './plans.module.scss'

const LANG = { ru: 'RU', en: 'EN', sa: 'SA' }

const ExpandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 10V4h6M20 14v6h-6M4 4l6 6M20 20l-6-6" />
  </svg>
)

// Вариант плана обучения (EducationPlan).
export const PlanCard = ({ plan, title, busy, onCreate, onOpenCourse, onFavorite, onExpand, createNote }) => {
  const modules = parsePlan(plan.text)
  const hasCourse = !!plan.quest?.id
  const count = plan.stages_count || modules.length
  return (
    <article className={cn(p.plan, { [p.planDone]: hasCourse })}>
      <div className={p.planHead}>
        <h3>{title}</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            className={cn(s.btn, s.ghost, s.icon)}
            aria-label="Открыть план целиком"
            onClick={onExpand}
          >
            <ExpandIcon />
          </button>
          {onFavorite && (
            <button
              type="button"
              className={cn(s.btn, s.ghost, s.icon, { [s.on]: plan.favorite })}
              aria-label={plan.favorite ? 'Убрать из избранного' : 'В избранное'}
              aria-pressed={!!plan.favorite}
              onClick={onFavorite}
            >
              {plan.favorite ? '★' : '☆'}
            </button>
          )}
        </div>
      </div>
      <div className={p.tags}>
        {hasCourse && <span className={cn(p.tag, p.tagOk)}>✓ Курс создан</span>}
        {count > 0 && <span className={p.tag}>{pluralN(count, ['модуль', 'модуля', 'модулей'])}</span>}
        {plan.language && <span className={p.tag}>{LANG[plan.language] || plan.language.toUpperCase()}</span>}
      </div>
      {/* полный план с прокруткой внутри карточки — как в прежней версии */}
      <div className={p.planScroll} tabIndex={0} aria-label="План целиком">
        {modules.length > 0 ? (
          <ol>
            {modules.map((m, i) => (
              <li key={i}>
                <b>{m.title}</b>
                {m.topics.length > 0 && (
                  <ul>
                    {m.topics.map((t, j) => (
                      <li key={j}>{t}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        ) : (
          <pre className={p.raw}>{plan.text || ''}</pre>
        )}
      </div>
      <div className={p.cta}>
        {hasCourse ? (
          <button type="button" className={cn(s.btn, s.sec)} onClick={onOpenCourse}>
            Перейти к курсу →
          </button>
        ) : (
          <>
            <button type="button" className={cn(s.btn, s.pri)} disabled={busy} onClick={onCreate}>
              Создать курс
            </button>
            {createNote && (
              <span className={cn(s.muted, s.small)} style={{ textAlign: 'center' }}>
                {createNote}
              </span>
            )}
          </>
        )}
      </div>
    </article>
  )
}

export const PlanGenerating = ({ title, lang }) => (
  <article className={p.plan} aria-busy="true">
    <div className={p.planHead}>
      <h3>{title}</h3>
      {lang && <span className={p.tag}>{lang}</span>}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className={s.small} style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>ИИ составляет план…</span>
        <span className={s.muted}>обычно до минуты</span>
      </div>
      <div className={p.progress}>
        <i />
      </div>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 6 }}>
      {[82, 64, 74, 58, 70].map((w) => (
        <div key={w} className={p.sk} style={{ width: `${w}%` }} />
      ))}
    </div>
    <div className={cn(s.muted, s.small)} style={{ marginTop: 'auto' }}>
      Можно уйти со страницы — план сохранится в этом навыке
    </div>
  </article>
)

export const PlanEmpty = ({ title, disabled, onGenerate, note }) => (
  <article className={cn(p.plan, p.empty)}>
    <div className={p.spark}>
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden="true"
      >
        <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" />
      </svg>
    </div>
    <h3>{title}</h3>
    <div className={s.muted} style={{ maxWidth: 280 }}>
      Получите ещё один план с другой структурой и подачей
    </div>
    <button type="button" className={cn(s.btn, s.pri)} disabled={disabled} onClick={onGenerate}>
      Сгенерировать вариант
    </button>
    {note && <div className={cn(s.muted, s.small)}>{note}</div>}
  </article>
)

export const PlanModal = ({ plan, title, open, onClose, footer }) => {
  const modules = parsePlan(plan?.text)
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={720}
      className={p.modal}
      destroyOnClose
      centered
    >
      <div className={s.vars}>
        <div className={p.modalBody}>
          <div className={p.planHead}>
            <h2>{title}</h2>
            <button type="button" className={cn(s.btn, s.ghost, s.icon)} aria-label="Закрыть" onClick={onClose}>
              ×
            </button>
          </div>
          <div className={p.modalList}>
            {modules.length ? (
              <ol>
                {modules.map((m, i) => (
                  <li key={i}>
                    <b>{m.title}</b>
                    {m.topics.length > 0 && (
                      <ul>
                        {m.topics.map((t, j) => (
                          <li key={j}>{t}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <pre className={p.raw}>{plan?.text}</pre>
            )}
          </div>
          {/* кнопка создания всегда видна внизу окна, список прокручивается над ней */}
          {footer && <div className={p.modalFoot}>{footer}</div>}
        </div>
      </div>
    </Modal>
  )
}
